import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Stack, Typography } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Page, Section, useSelectedOrganisation, Widget } from '../../../shared';
import { useRelations, useSaveSupportCase } from '../../hooks';
import { supportCaseFromFormMapper } from '../../mappers/support-case.mapper';
import { ISupportCaseForm } from '../../models';
import { useSupportCaseSchema } from '../../validators';
import { SupporteeInputs } from '../../components/support-case-create/supportee-inputs.component';
import { SupportCaseDetailInputs } from '../../components/support-case-create/support-case-detail-inputs.component';
import { SupportCaseAddressInputs } from '../../components/support-case-create/support-case-address-inputs.component';
import { SupportCaseContactInputs } from '../../components/support-case-create/support-case-contact-inputs.component';

export function SupportCaseCreatePage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { shareSupportCases } = window.carity.environment;

    const schema = useSupportCaseSchema();
    const form = useForm<ISupportCaseForm>({
        resolver: yupResolver(schema),
        mode: 'all',
        defaultValues: {
            address: { country: { code: 'be', label: 'België' } },
        },
    });

    const { mutateAsync } = useSaveSupportCase();
    const { organisation } = useSelectedOrganisation();
    const relations = useRelations();

    const onSubmit = async (data: ISupportCaseForm) => {
        if (organisation) {
            const savedSupportCase = await mutateAsync(supportCaseFromFormMapper(data, organisation?.id, relations));
            navigate(`/support/support-cases/${savedSupportCase.id}/detail`);
        }
    };

    const actions = [
        <Button onClick={() => navigate(-1)} color="secondary">
            {t('cancel')}
        </Button>,
        <Button type="submit" variant="contained" color="primary" onClick={form.handleSubmit(onSubmit)}>
            {t('save')}
        </Button>,
    ];

    return (
        <Page title={t('newSupportCase')} actions={actions}>
            <FormProvider {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} noValidate={true} autoComplete="off">
                    <Widget footer={[...actions].reverse()}>
                        <Stack spacing={2}>
                            {shareSupportCases && (
                                <Section title={t('organisation')}>
                                    <Typography>{organisation?.name || ''}</Typography>
                                </Section>
                            )}

                            <Section title={t('primarySupportee')}>
                                <SupporteeInputs field="primarySupportee" />
                            </Section>

                            <SupportCaseDetailInputs />
                            <SupportCaseAddressInputs />
                            <SupportCaseContactInputs />
                        </Stack>
                    </Widget>
                </form>
            </FormProvider>
        </Page>
    );
}
